import {
  CheckCircle,
  Close,
  CloudDownload,
  CloudUpload,
  DescriptionOutlined,
  RemoveRedEye,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { isCashPayment } from "./constants";
import {
  classifyDocUrl,
  applyDecision,
  isSaveComplete,
  isApproveDisabled,
  isDeclineDisabled,
  buildIpdUpdatePayload,
  buildGenericFieldPayload,
  formatToDateTimeLocal,
} from "./pureLogic";
import * as api from "./billCollection.service";
import CollectionUpdation from "./CollectionUpdation";
import CollectionHistory from "./CollectionHistory";

const isViewerOnly = () => localStorage.getItem("viewer") === "true";

const DocumentActionsTab = ({
  data,
  urlArray,
  isDisable,
  isDetailedBillRequired,
  isFinalBill,
  // Lens gate — forwarded to CollectionUpdation → Collectible sub-tab.
  isLensApplicable = false,
  hasLensDecision = false,
  lensChoice = "",
  lensSaved = false,
  lensLocked = false,
  onLensChoice,
  onReopenLensModal,
  onSaveData,
  onRefresh,
  onUpdateIpdNo,
  onUpdateInfo,
  onGoToUpload,
  onStatusChange,
  onApprovalSuccess,
}) => {
  const [activeFileTab, setActiveFileTab] = useState("pdf");
  const [activeRightTab, setActiveRightTab] = useState("verification");
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [signedDocs, setSignedDocs] = useState([]);
  const [finalStatusArray, setFinalStatusArray] = useState([]);
  const [finalBillAmount, setFinalBillAmount] = useState(
    data?.finalBillObj?.finalBillAmount ?? null
  );
  const [netBillAmount, setNetBillAmount] = useState(
    data?.finalBillObj?.netBillAmount ?? null
  );
  const [ipdNoOnBill, setIpdNoOnBill] = useState(data?.ipdNoOnBill || "");
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineIndex, setDeclineIndex] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  const [conditionalModalOpen, setConditionalModalOpen] = useState(false);
  const [conditionalIndex, setConditionalIndex] = useState(null);
  const [conditionalReason, setConditionalReason] = useState("");

  const [form, setForm] = useState({
    actualOtStartTime: data?.actualOtStartTime || "",
    actualOtEndTime: data?.actualOtEndTime || "",
    discharge_date: data?.discharge_date || "",
    actual_admission_date: data?.actual_admission_date || "",
    totalCollectibleAtAdmission: data?.totalCollectibleAtAdmission || "",
    totalCollectibleAtDischarge: data?.totalCollectibleAtDischarge || "",
    totalCollectedAtAdmission: data?.totalCollectedAtAdmission || "",
    totalCollectedAtDischarge: data?.totalCollectedAtDischarge || "",
  });

  const paymentMode = data?.payment_mode || "";
  const isCash = isCashPayment(paymentMode);
  const appId = data?.appID || "";
  const leadId = data?.leadId || data?.lead_id || "";
  const userObj = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = userObj?.userId || localStorage.getItem("userId") || "";
  const userName = userObj?.username || userObj?.name || localStorage.getItem("userName") || "";

  // Row-derived fields needed for updateOverallStatus (same source the central
  // dashboard reads off the approval row). Sensible fallbacks for the combined
  // final-bill flow.
  const tabName = data?.tab_name || "postdischarge";
  const overallKey = data?.key || "finalBill";
  const documentId = data?.document_id || data?.documentId || "";
  const cbObjectId = data?.cb_objectId || "";

  // updateInsDashboard (net bill) fires from the Verification & Action flow
  // (approve/decline/save), only for Cash, and only when a net bill amount was
  // entered here. Fires on every such action. Fire-and-continue (non-blocking).
  const updateInsDashboardOnce = useCallback(() => {
    if (!isCash) return;
    if (netBillAmount === null || netBillAmount === undefined || String(netBillAmount).trim() === "") return;
    api
      .updateInsDashboard({
        netBillAmount: Number(netBillAmount),
        userId,
        userName,
        appId,
      })
      .catch(() => {
        /* non-blocking: don't block the decision flow on failure */
      });
  }, [isCash, netBillAmount, userId, userName, appId]);

  // Persist a single document's Approve/Decline decision to the server via
  // updateOverallStatus — fires on the decision action itself (parity with the
  // central dashboard), alongside updateInsDashboardOnce(). The modal must NOT
  // close here (it only closes on Send for Approval success).
  const persistDecision = useCallback(
    (statusObj) => {
      const reqObj = {
        tab_name: tabName,
        document_id: documentId,
        key: overallKey,
        app_id: appId,
        url_id: [statusObj],
        cb_objectId: cbObjectId,
        user_id: userId,
        user_name: userName,
        source: "Central Dashboard",
      };
      if (isCash) {
        reqObj.finalBillObj = { finalBillAmount };
        reqObj.isInsFBStatus = "Approved";
      }
      return api
        .updateOverallStatus(reqObj)
        .then((res) => {
          if (res?.status === 200) {
            toast.success("Documents Status Updated Successfully");
            return true;
          }
          return false;
        })
        .catch(() => {
          toast.error("Something Went Wrong");
          return false;
        });
    },
    [tabName, documentId, overallKey, appId, cbObjectId, userId, userName, isCash, finalBillAmount]
  );

  // Re-derive all prefilled fields whenever `data` changes (parity with Angular
  // `fetchAdmissionCollectible()`, which re-runs after the row loads). useState
  // initializers only run on mount, but `data` arrives AFTER the async
  // fetchFinalBill/getFinalBillCentral resolve — so the form must re-sync here or
  // the Actions tab would never prefill.
  useEffect(() => {
    if (!data) return;
    setFinalBillAmount(
      data?.finalBillObj?.finalBillAmount ?? data?.finalBillAmount ?? null
    );
    setNetBillAmount(
      data?.finalBillObj?.netBillAmount ?? data?.netBillAmount ?? null
    );
    setIpdNoOnBill(
      typeof data?.ipdNoOnBill === "object"
        ? data?.ipdNoOnBill?.value || ""
        : data?.ipdNoOnBill || ""
    );
    setForm({
      actualOtStartTime: data?.actualOtStartTime || "",
      actualOtEndTime: data?.actualOtEndTime || "",
      discharge_date: data?.discharge_date || "",
      actual_admission_date: data?.actual_admission_date || "",
      totalCollectibleAtAdmission: data?.totalCollectibleAtAdmission || "",
      totalCollectibleAtDischarge: data?.totalCollectibleAtDischarge || "",
      totalCollectedAtAdmission: data?.totalCollectedAtAdmission || "",
      totalCollectedAtDischarge: data?.totalCollectedAtDischarge || "",
    });
  }, [data]);

  // Initialize signed docs + seed decisions from any existing per-doc statuses
  // (parity with Angular initActionsTab).
  useEffect(() => {
    setSignedDocs(
      (urlArray || []).map((doc) => ({
        ...doc,
        urlSign: null,
        isImage: false,
      }))
    );
    const seeded = [];
    (urlArray || []).forEach((doc, i) => {
      const status = doc?.status || "";
      if (/approve/gi.test(status)) {
        seeded[i] = { _id: doc._id, status: "Approved" };
      } else if (/decline/gi.test(status)) {
        seeded[i] = { _id: doc._id, status: "Declined", declineReason: doc.declineReason };
      }
    });
    setFinalStatusArray(seeded);
    if (urlArray?.length) loadDoc(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlArray]);

  const loadDoc = useCallback(
    async (index) => {
      const doc = (urlArray || [])[index];
      if (!doc?.url) return;
      // Reuse an already-signed URL (idempotent) — do not re-sign.
      if (signedDocs[index]?.urlSign) {
        setCurrentDocIndex(index);
        return;
      }
      setPdfLoading(true);
      try {
        const res = await api.getSignedUrl([doc.url]);
        const signed = res?.result?.[0] || res?.[0] || doc.url;
        const { isImage: isImg, viewerUrl } = classifyDocUrl(doc.url, signed);
        setSignedDocs((prev) => {
          const copy = [...prev];
          copy[index] = { ...copy[index], urlSign: viewerUrl, isImage: isImg };
          return copy;
        });
        setCurrentDocIndex(index);
      } catch {
        toast.error("Failed to load document");
      } finally {
        setPdfLoading(false);
      }
    },
    [urlArray, signedDocs]
  );

  const downloadAttachment = async (item) => {
    try {
      const res = await api.getSignedUrl([item.url]);
      const signedUrl = res?.result?.[0] || res?.[0] || item.url;
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = item.name || "document";
      link.target = "_blank";
      link.click();
    } catch {
      toast.error("Download failed");
    }
  };

  const onFieldChange = async (fieldName, value) => {
    if (value === null || value === undefined || value === "") return;
    try {
      const payload = buildGenericFieldPayload(
        appId,
        leadId,
        fieldName,
        value,
        userId,
        userName
      );
      const res = await api.updateCentralDashReporting(payload);
      if (res?.status === 200 || res?.status === undefined) {
        toast.success(`${fieldName} updated`);
        onUpdateInfo?.(fieldName);
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const updateIPD = async () => {
    if (!ipdNoOnBill) return;
    try {
      const payload = buildIpdUpdatePayload(appId, ipdNoOnBill, userId);
      await api.updateCentralDashReporting(payload);
      toast.success("IPD updated");
      onRefresh?.();
      onUpdateIpdNo?.(ipdNoOnBill);
    } catch {
      toast.error("IPD update failed");
    }
  };

  // Net Bill Amount is mandatory for Cash. Blocks Approve, Decline/Conditional,
  // and Save Data until it is entered (central parity). Non-Cash is unaffected.
  const isNetBillMissing = useCallback(() => {
    if (!isCash) return false;
    if (netBillAmount === null || netBillAmount === undefined || String(netBillAmount).trim() === "") {
      toast.error("Net Bill Amount is required");
      return true;
    }
    return false;
  }, [isCash, netBillAmount]);

  const changeStatus = (status, docId, index) => {
    // Viewer-only sessions cannot record decisions.
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    // Net Bill Amount mandatory gate (Cash) — covers Approve and Decline/Conditional.
    if (isNetBillMissing()) return;
    if (status === "Approved") {
      if (isCash && finalBillAmount === null) {
        toast.error("Final Bill Amount is required");
        return;
      }
      if (!window.confirm(`Approve this document?`)) return;
      const statusObj = { _id: docId, status: "Approved" };
      applyStatus(index, statusObj);
      toast.success("Document Approved");
      // Persist the decision (updateOverallStatus) AND fire the net bill update
      // (once) on the decision action. The modal must NOT close here — it only
      // closes on Send for Approval success.
      persistDecision(statusObj).then((ok) => {
        if (ok) updateInsDashboardOnce();
      });
    } else if (status === "Declined") {
      setDeclineIndex(index);
      setDeclineReason("");
      setDeclineModalOpen(true);
    } else if (status === "Conditional") {
      setConditionalIndex(index);
      setConditionalReason("");
      setConditionalModalOpen(true);
    }
  };

  const confirmDecline = () => {
    if (!declineReason.trim()) {
      toast.info("Please enter a decline reason");
      return;
    }
    const doc = urlArray[declineIndex];
    const statusObj = {
      _id: doc?._id,
      status: "Declined",
      declineReason,
    };
    applyStatus(declineIndex, statusObj);
    toast.success("Document Declined");
    setDeclineModalOpen(false);
    // Persist the decision (updateOverallStatus) AND fire the net bill update
    // (once) — only after the decline reason is captured. The modal must NOT
    // close here — it only closes on Send for Approval success.
    persistDecision(statusObj).then((ok) => {
      if (ok) updateInsDashboardOnce();
    });
  };

  const confirmConditional = () => {
    if (!conditionalReason.trim()) {
      toast.info("Please enter a reason");
      return;
    }
    const doc = urlArray[conditionalIndex];
    const statusObj = {
      _id: doc?._id,
      status: "conditionallyApproved",
      reason: conditionalReason,
    };
    applyStatus(conditionalIndex, statusObj);
    toast.success("Document Conditionally Approved");
    setConditionalModalOpen(false);
    persistDecision(statusObj).then((ok) => {
      if (ok) updateInsDashboardOnce();
    });
  };

  const applyStatus = (index, statusObj) => {
    setFinalStatusArray((prev) => applyDecision(prev, index, statusObj));
  };

  const handleSaveData = () => {
    // Net Bill Amount mandatory gate (Cash) — blocks Save Data too.
    if (isNetBillMissing()) return;
    if (isCash && finalBillAmount === null) {
      toast.error("Final Bill Amount is required");
      return;
    }
    if (!isSaveComplete(finalStatusArray, (urlArray || []).length)) {
      toast.info("Please Review All Images");
      return;
    }
    // Net bill update also fires (once) on Save Data.
    const savePromise = onSaveData?.({
      status: "",
      data: finalStatusArray,
      finalBillAmount,
    });
    if (savePromise && typeof savePromise.then === 'function') {
      savePromise.then((ok) => {
        if (ok) updateInsDashboardOnce();
      });
    } else {
      updateInsDashboardOnce();
    }
  };

  const currentDoc = signedDocs[currentDocIndex] || {};

  if (!urlArray?.length) {
    return (
      <div className="cbc-actions-empty">
        <DescriptionOutlined className="cbc-actions-empty-icon" />
        <div className="cbc-actions-empty-title">No documents available</div>
        <div className="cbc-actions-empty-sub">
          Upload documents first from the "Document Upload" tab to start reviewing
          and taking actions
        </div>
        <button
          className="cbc-btn outline"
          style={{ marginTop: 14 }}
          onClick={() => onGoToUpload?.()}
        >
          <CloudUpload sx={{ fontSize: 16, mr: 0.5 }} /> Go to Document Upload
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div className="cbc-two-col">
        {/* ── Left: File Viewer ── */}
        <div className="cbc-card">
          <div className="cbc-tabs">
            <button
              className={`cbc-tab ${activeFileTab === "pdf" ? "active" : ""}`}
              onClick={() => setActiveFileTab("pdf")}
            >
              View PDF
            </button>
            <button
              className={`cbc-tab ${activeFileTab === "attachments" ? "active" : ""}`}
              onClick={() => setActiveFileTab("attachments")}
            >
              Attachments ({(urlArray || []).length})
            </button>
          </div>

          {activeFileTab === "pdf" && (
            <div className="cbc-viewer">
              {pdfLoading ? (
                <div className="cbc-loading">
                  <CircularProgress size={28} />
                </div>
              ) : currentDoc.urlSign ? (
                currentDoc.isImage ? (
                  <img src={currentDoc.urlSign} alt="Document" />
                ) : (
                  <iframe src={currentDoc.urlSign} title="Document viewer" />
                )
              ) : (
                <div className="cbc-empty">Select a document to view</div>
              )}
            </div>
          )}

          {activeFileTab === "attachments" && (
            <div className="cbc-card-body">
              {(urlArray || []).map((item, i) => (
                <div className="cbc-doc-row" key={item.url + i}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {item.name || `Document ${i + 1}`}
                    </div>
                    {item.clickedBy && (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {item.clickedBy}
                        {item.timestamp ? ` | ${item.timestamp}` : ""}
                      </div>
                    )}
                    {item.status && (
                      <span
                        className={`cbc-pill ${
                          item.status === "Approved"
                            ? "green"
                            : item.status === "Declined"
                            ? "red"
                            : "yellow"
                        }`}
                        style={{ marginTop: 4 }}
                      >
                        {item.status}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      className="cbc-btn outline"
                      style={{ padding: "4px 8px", minHeight: 32 }}
                      onClick={() => {
                        setActiveFileTab("pdf");
                        loadDoc(i);
                      }}
                      title="View"
                    >
                      <RemoveRedEye sx={{ fontSize: 16 }} />
                    </button>
                    <button
                      className="cbc-btn outline"
                      style={{ padding: "4px 8px", minHeight: 32 }}
                      onClick={() => downloadAttachment(item)}
                      title="Download"
                    >
                      <CloudDownload sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: Info + Sub-tabs ── */}
        <div className="cbc-card">
          {/* Patient & Visit Information card (central .bad-info-card parity) */}
          <div className="cbc-info-card">
            <div className="cbc-info-head">
              <span className="cbc-info-title">PATIENT &amp; VISIT INFORMATION</span>
            </div>
            <div className="cbc-info-grid">
              <div className="cbc-info-item">
                <span className="cbc-info-label">Patient Name</span>
                <span className="cbc-info-value">{data?.name || data?.patient_name || "-"}</span>
              </div>
              <div className="cbc-info-item">
                <span className="cbc-info-label">Patient City</span>
                <span className="cbc-info-value">{data?.city || "-"}</span>
              </div>
              <div className="cbc-info-item">
                <span className="cbc-info-label">Hospital Name</span>
                <span className="cbc-info-value">{data?.hospital || data?.hospital_name || "-"}</span>
              </div>
              <div className="cbc-info-item">
                <span className="cbc-info-label">Payment Mode</span>
                <span className="cbc-info-value accent">{paymentMode || "-"}</span>
              </div>
            </div>
          </div>

          {/* Sub-tabs (orange-boxed parent tabs — central parity) */}
          <div className="cbc-tabs cbc-rtabs">
            <button
              className={`cbc-tab ${activeRightTab === "verification" ? "active" : ""}`}
              onClick={() => setActiveRightTab("verification")}
            >
              Verification & Action
            </button>
            {isFinalBill && (
              <>
                <button
                  className={`cbc-tab ${activeRightTab === "collection" ? "active" : ""}`}
                  onClick={() => setActiveRightTab("collection")}
                >
                  Collection Updation
                </button>
                <button
                  className={`cbc-tab ${activeRightTab === "history" ? "active" : ""}`}
                  onClick={() => setActiveRightTab("history")}
                >
                  History
                </button>
              </>
            )}
          </div>

          <div className="cbc-card-body">
            {activeRightTab === "verification" && (
              <VerificationPanel
                isFinalBill={isFinalBill}
                isCash={isCash}
                isDisable={isDisable}
                isDetailedBillRequired={isDetailedBillRequired}
                ipdNoOnBill={ipdNoOnBill}
                setIpdNoOnBill={setIpdNoOnBill}
                updateIPD={updateIPD}
                finalBillAmount={finalBillAmount}
                setFinalBillAmount={setFinalBillAmount}
                netBillAmount={netBillAmount}
                setNetBillAmount={setNetBillAmount}
                form={form}
                setForm={setForm}
                onFieldChange={onFieldChange}
                formatToDateTimeLocal={formatToDateTimeLocal}
                urlArray={urlArray}
                finalStatusArray={finalStatusArray}
                changeStatus={changeStatus}
                handleSaveData={handleSaveData}
              />
            )}
            {activeRightTab === "collection" && isFinalBill && (
              <CollectionUpdation
                appointmentId={appId}
                leadId={leadId}
                collectionStatus={
                  typeof data?.collectionStatus === "object"
                    ? data.collectionStatus?.status
                    : data?.collectionStatus
                }
                paymentMode={paymentMode}
                isLensApplicable={isLensApplicable}
                hasLensDecision={hasLensDecision}
                lensChoice={lensChoice}
                lensSaved={lensSaved}
                lensLocked={lensLocked}
                onLensChoice={onLensChoice}
                onReopenLensModal={onReopenLensModal}
                onStatusChange={onStatusChange}
                onApprovalSuccess={onApprovalSuccess}
              />
            )}
            {activeRightTab === "history" && isFinalBill && (
              <CollectionHistory appointmentId={appId} />
            )}
          </div>
        </div>
      </div>

      {/* Save Data button — only for the Verification & Action tab (it saves the
          per-document decisions). Hidden on Collection Updation / History, which
          have their own actions (e.g. Send for Approval). Central parity. */}
      {activeRightTab === "verification" && (
        <div style={{ textAlign: "right", marginTop: 12 }}>
          <button
            className="cbc-btn primary full-mobile"
            onClick={handleSaveData}
          >
            Save Data
          </button>
        </div>
      )}

      {/* Decline Reason Dialog */}
      <Dialog
        open={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Decline Reason
          <IconButton onClick={() => setDeclineModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <textarea
            className="cbc-input"
            rows={3}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter reason for declining..."
            style={{ resize: "vertical", minHeight: 80 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button className="cbc-btn outline" onClick={() => setDeclineModalOpen(false)}>
              Cancel
            </button>
            <button className="cbc-btn danger" onClick={confirmDecline}>
              Decline
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conditional Approve Dialog */}
      <Dialog
        open={conditionalModalOpen}
        onClose={() => setConditionalModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Conditional Approve Reason
          <IconButton onClick={() => setConditionalModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <textarea
            className="cbc-input"
            rows={3}
            value={conditionalReason}
            onChange={(e) => setConditionalReason(e.target.value)}
            placeholder="Enter reason for conditional approval..."
            style={{ resize: "vertical", minHeight: 80 }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button className="cbc-btn outline" onClick={() => setConditionalModalOpen(false)}>
              Cancel
            </button>
            <button className="cbc-btn success" onClick={confirmConditional}>
              Approve
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentActionsTab;

// ── Verification & Action sub-panel ──
function VerificationPanel({
  isFinalBill,
  isCash,
  isDisable,
  isDetailedBillRequired,
  ipdNoOnBill,
  setIpdNoOnBill,
  updateIPD,
  finalBillAmount,
  setFinalBillAmount,
  netBillAmount,
  setNetBillAmount,
  form,
  setForm,
  onFieldChange,
  formatToDateTimeLocal,
  urlArray,
  finalStatusArray,
  changeStatus,
  handleSaveData,
}) {
  return (
    <div>
      {/* Detail Bill Required note — shown only when the API flags
          isDetailedBillRequired === true (central parity). */}
      {isDetailedBillRequired && (
        <div className="cu-note" style={{ marginBottom: 14 }}>
          <span className="cu-note-icon">&#9888;</span>
          Detail Bill Required
        </div>
      )}

      {/* Bill Details */}
      {isFinalBill && (
        <>
          <div className="cbc-section-title">Bill Details</div>
          <div className="cbc-form-grid">
            <div>
              <label className="cbc-label">IPD No. on Bill</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  className="cbc-input"
                  value={ipdNoOnBill}
                  onChange={(e) => setIpdNoOnBill(e.target.value)}
                />
                <button
                  className="cbc-btn primary"
                  style={{ padding: "6px 12px", whiteSpace: "nowrap" }}
                  onClick={updateIPD}
                >
                  Update
                </button>
              </div>
            </div>
            {isCash && (
              <>
                <div>
                  <label className="cbc-label">Final Bill Amount *</label>
                  <input
                    type="number"
                    className="cbc-input"
                    value={finalBillAmount ?? ""}
                    onChange={(e) =>
                      setFinalBillAmount(e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </div>
                <div>
                  <label className="cbc-label">Net Bill Amount *</label>
                  <input
                    type="number"
                    className="cbc-input"
                    value={netBillAmount ?? ""}
                    onChange={(e) =>
                      setNetBillAmount(e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Timings */}
      <div className="cbc-section-title">Admission, Discharge & OT Timings</div>
      <div className="cbc-form-grid">
        {[
          { key: "actual_admission_date", label: "Admission Date" },
          { key: "discharge_date", label: "Discharge Date" },
          { key: "actualOtStartTime", label: "OT Start Time" },
          { key: "actualOtEndTime", label: "OT End Time" },
        ].map(({ key, label }) => (
          <div key={key}>
            <label className="cbc-label">{label}</label>
            <input
              type="datetime-local"
              className="cbc-input"
              value={formatToDateTimeLocal(form[key])}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, [key]: e.target.value }));
                onFieldChange(key, e.target.value);
              }}
            />
          </div>
        ))}
      </div>

      {/* Collection Summary */}
      <div className="cbc-section-title">Collection Summary</div>
      <div className="cbc-form-grid">
        <div>
          <label className="cbc-label">Total Collectible at Admission</label>
          <input
            className="cbc-input"
            value={form.totalCollectibleAtAdmission}
            readOnly
            disabled
          />
        </div>
        <div>
          <label className="cbc-label">Total Collectible at Discharge</label>
          <input
            className="cbc-input"
            value={form.totalCollectibleAtDischarge}
            readOnly
            disabled
          />
        </div>
        <div>
          <label className="cbc-label">Total Collected at Admission</label>
          <input
            type="number"
            className="cbc-input"
            value={form.totalCollectedAtAdmission}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                totalCollectedAtAdmission: e.target.value,
              }))
            }
            onBlur={(e) => onFieldChange("totalCollectedAtAdmission", e.target.value)}
          />
        </div>
        <div>
          <label className="cbc-label">Total Collected at Discharge</label>
          <input
            type="number"
            className="cbc-input"
            value={form.totalCollectedAtDischarge}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                totalCollectedAtDischarge: e.target.value,
              }))
            }
            onBlur={(e) => onFieldChange("totalCollectedAtDischarge", e.target.value)}
          />
        </div>
      </div>

      {/* Document Decision */}
      <div className="cbc-section-title">Document Decision</div>
      {(urlArray || []).map((doc, i) => {
        const decision = finalStatusArray[i];
        return (
          <div
            className="cbc-doc-row"
            key={doc.url + i}
            style={{ alignItems: "center" }}
          >
            <div style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>
              {doc.name || `Document ${i + 1}`}
              {(() => {
                // Show the current decision if any, else the doc's own status (from the row).
                const shownStatus = decision?.status || doc.status;
                if (!shownStatus) return null;
                const cls =
                  shownStatus === "Approved"
                    ? "green"
                    : /decline/i.test(shownStatus)
                    ? "red"
                    : "yellow";
                return (
                  <span className={`cbc-pill ${cls}`} style={{ marginLeft: 8 }}>
                    {shownStatus === "conditionallyApproved" ? "Conditionally Approved" : shownStatus}
                    {doc.source === "Central Dashboard" ? " (By Central Dashboard)" : ""}
                  </span>
                );
              })()}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="cbc-btn success"
                style={{ padding: "4px 12px", minHeight: 32, fontSize: 12 }}
                onClick={() => changeStatus("Approved", doc._id, i)}
                disabled={isApproveDisabled(
                  { status: decision?.status || doc.status, source: doc.source },
                  isDisable
                )}
                title="Approve"
              >
                <ThumbUp sx={{ fontSize: 14 }} /> Approve
              </button>
              <button
                className="cbc-btn danger"
                style={{ padding: "4px 12px", minHeight: 32, fontSize: 12 }}
                onClick={() => changeStatus("Declined", doc._id, i)}
                disabled={isDeclineDisabled(
                  { status: decision?.status || doc.status, source: doc.source },
                  isDisable
                )}
                title="Decline"
              >
                <ThumbDown sx={{ fontSize: 14 }} /> Decline
              </button>
              <button
                className="cbc-btn outline"
                style={{ padding: "4px 12px", minHeight: 32, fontSize: 12 }}
                onClick={() => changeStatus("Conditional", doc._id, i)}
                disabled={
                  isDisable ||
                  decision?.status === "Conditionally Approved" ||
                  decision?.status === "Approved"
                }
                title="Conditional Approve"
              >
                Conditional
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
